/**
 * Wizard draft model + step composition (design §7.1, §7.3).
 *
 * The frame enforces exactly one rule: `steps = [basics, model, …template
 * steps verbatim, review]` — template step order is the contract, the frame
 * never adds, reorders, or removes template steps (H3). Duplicate step `id`s
 * dedupe by first occurrence; an identical `id` with a different `type` is a
 * template data error — the later step renders the unknown-step placeholder.
 */
import type {
  AgentCoderConfig,
  AgentTemplate,
  AgentTemplateWizardStepType,
  TemplateWizardStep,
  WizardDraft,
  WizardForwardingOverride,
} from '../types';

export const FRAME_TITLES = {
  basics: 'Name and instructions',
  model: 'Model',
  review: 'Review',
} as const;

export type WizardEntry =
  | { kind: 'frame'; key: 'basics' | 'model' | 'review'; title: string }
  | { kind: 'template'; key: string; step: TemplateWizardStep; forceUnknown: boolean };

export function composeWizardEntries(template: AgentTemplate): WizardEntry[] {
  const entries: WizardEntry[] = [
    { kind: 'frame', key: 'basics', title: FRAME_TITLES.basics },
    { kind: 'frame', key: 'model', title: FRAME_TITLES.model },
  ];

  const seen = new Map<string, string>();
  for (const [index, step] of (template.wizardSteps ?? []).entries()) {
    const seenType = seen.get(step.id);
    if (seenType !== undefined) {
      if (seenType === step.type) {
        continue; // duplicate of the first occurrence — deduped away.
      }
      // Same id, different type → data error: render as unknown (§7.1).
      entries.push({ kind: 'template', key: `${step.id}~${index}`, step, forceUnknown: true });
      continue;
    }
    seen.set(step.id, step.type);
    entries.push({ kind: 'template', key: step.id, step, forceUnknown: false });
  }

  entries.push({ kind: 'frame', key: 'review', title: FRAME_TITLES.review });
  return entries;
}

export function initWizardDraft(template: AgentTemplate, projectId?: string): WizardDraft {
  return {
    basics: { name: '', instructions: template.defaultInstructions ?? '' },
    model: { modelCollectionId: null },
    memory: { attach: template.memory?.attachStorageMemory === true },
    policies: { statements: [] },
    triggers: { items: [] },
    steps: {},
    projectId,
  };
}

/**
 * attach-storage / attach-policy / attach-webhook renderers operate on the
 * draft's NAMED slices (design §9 — review + the post-create attach sequence
 * consume the slices, and step ids are not stable across templates). Every
 * other type uses the generic per-step store `draft.steps[key]` — including
 * the P4/P5 registered steps, which the frame consumes via the collect
 * helpers below (`forwarding` → create `overrides.forwarding`, `bind-number`
 * → post-create bind, coder steps → create `overrides.coderConfig`, P6-T3)
 * rather than through named slices.
 */
const NAMED_SLICE_BY_TYPE: Partial<
  Record<AgentTemplateWizardStepType, 'memory' | 'policies' | 'triggers'>
> = {
  'attach-storage': 'memory',
  'attach-policy': 'policies',
  'attach-webhook': 'triggers',
};

export function stepValue(draft: WizardDraft, entry: WizardEntry): unknown {
  if (entry.kind !== 'template') {
    return undefined;
  }
  const slice = NAMED_SLICE_BY_TYPE[entry.step.type];
  return slice ? draft[slice] : draft.steps[entry.key];
}

export function applyStepChange(
  draft: WizardDraft,
  entry: WizardEntry,
  next: unknown,
): WizardDraft {
  if (entry.kind !== 'template') {
    return draft;
  }
  const slice = NAMED_SLICE_BY_TYPE[entry.step.type];
  if (slice) {
    return { ...draft, [slice]: next } as WizardDraft;
  }
  return { ...draft, steps: { ...draft.steps, [entry.key]: next } };
}

// ---------------------------------------------------------------------------
// Registered-step consumption (P4 forwarding / bind-number, P5 Q2 review rows)
// ---------------------------------------------------------------------------

const templateEntries = (entries: WizardEntry[]) =>
  entries.filter(
    (entry): entry is Extract<WizardEntry, { kind: 'template' }> => entry.kind === 'template',
  );

/** Structural check mirroring `isForwardingStepValue` (numbers/types.ts:139) — local
 * on purpose: the frame must not runtime-depend on `features/numbers`. */
function asForwardingOverride(value: unknown): WizardForwardingOverride | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const v = value as Record<string, unknown>;
  if (typeof v.enabled !== 'boolean' || !v.audioOut || typeof v.audioOut !== 'object') {
    return undefined;
  }
  const audioOut = v.audioOut as Record<string, unknown>;
  const override: WizardForwardingOverride = {
    enabled: v.enabled,
    audioOut: {
      enabled: audioOut.enabled === true,
      ...(typeof audioOut.voiceId === 'string' && audioOut.voiceId
        ? { voiceId: audioOut.voiceId }
        : {}),
    },
  };
  if (typeof v.greeting === 'string' && v.greeting !== '') {
    override.greeting = v.greeting;
  }
  return override;
}

/**
 * The `forwarding` step value merged into create `overrides.forwarding` (OQ-3).
 * The renderer never seeds `draft.steps` on mount (forwarding.step.tsx:115-119
 * only reports errors), so an untouched step yields `undefined` and the
 * template default stays authoritative. First valid value wins across
 * duplicates.
 */
export function collectForwardingOverride(
  draft: WizardDraft,
  entries: WizardEntry[],
): WizardForwardingOverride | undefined {
  for (const entry of templateEntries(entries)) {
    if (entry.step.type !== 'forwarding' || entry.forceUnknown) {
      continue;
    }
    const override = asForwardingOverride(draft.steps[entry.key]);
    if (override) {
      return override;
    }
  }
  return undefined;
}

/** Structural read of one coder step value — local on purpose (the frame must
 * not runtime-depend on `features/coder`; mirrors the coder slice guards
 * coder/types.ts:177/192/207 field-by-field without importing them). */
function str(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * The coder steps' persisted values merged into create `overrides.coderConfig`
 * (P6-T3). Select-Image (draft.ts step ids `sandbox-image`/coder seed),
 * Attach-Repository and Attach-Secrets store into `draft.steps[key]`
 * (`SelectImageValue.imageId`, `AttachRepositoryValue.repositoryId` +
 * `gitBranchName` → `branchName`, first non-empty
 * `AttachSecretsValue.references[].vaultId` → `secretsVaultId` — vault
 * REFERENCES only, secret values never transit the browser, P5-2 §12).
 * Untouched steps yield absent fields; an all-empty result yields `undefined`
 * so the template default (select-image `options.defaultImage`) stays
 * authoritative server-side. First valid value wins across duplicates.
 */
export function collectCoderConfigOverride(
  draft: WizardDraft,
  entries: WizardEntry[],
): AgentCoderConfig | undefined {
  const config: AgentCoderConfig = {};
  for (const entry of templateEntries(entries)) {
    if (entry.forceUnknown) {
      continue;
    }
    const value = draft.steps[entry.key] as Record<string, unknown> | undefined;
    if (!value || typeof value !== 'object') {
      continue;
    }
    if (entry.step.type === 'select-image' && !config.imageId) {
      const imageId = str(value.imageId);
      if (imageId) config.imageId = imageId;
    }
    if (entry.step.type === 'attach-repository') {
      if (!config.repositoryId) {
        const repositoryId = str(value.repositoryId);
        if (repositoryId) config.repositoryId = repositoryId;
      }
      if (!config.branchName) {
        const branchName = str(value.gitBranchName);
        if (branchName) config.branchName = branchName;
      }
    }
    if (entry.step.type === 'attach-secrets' && !config.secretsVaultId) {
      const references = Array.isArray(value.references) ? value.references : [];
      for (const reference of references) {
        const vaultId =
          reference && typeof reference === 'object'
            ? str((reference as { vaultId?: unknown }).vaultId)
            : undefined;
        if (vaultId) {
          config.secretsVaultId = vaultId;
          break;
        }
      }
    }
  }
  return Object.keys(config).length > 0 ? config : undefined;
}

/**
 * `bind-number` intents (design §12.2) — executed post-create by the §15.3
 * sequence (`POST /v1/voice-bridge/numbers/:id/bind {factoryId}`), one per
 * step entry that holds a non-empty `numberId`.
 */
export function collectBindNumberIntents(
  draft: WizardDraft,
  entries: WizardEntry[],
): { entryKey: string; numberId: string }[] {
  const intents: { entryKey: string; numberId: string }[] = [];
  for (const entry of templateEntries(entries)) {
    if (entry.step.type !== 'bind-number' || entry.forceUnknown) {
      continue;
    }
    const value = draft.steps[entry.key] as { numberId?: unknown } | undefined;
    if (typeof value?.numberId === 'string' && value.numberId !== '') {
      intents.push({ entryKey: entry.key, numberId: value.numberId });
    }
  }
  return intents;
}

const clip = (text: string, max = 48) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/**
 * Generic-store step types that get a Review summary row (P5 §25 Q2). The
 * named-slice types (attach-storage/policy/webhook) already have dedicated
 * Review rows and are excluded here to avoid double-reporting.
 */
export const REVIEW_SUMMARY_STEP_TYPES: readonly AgentTemplateWizardStepType[] = [
  'text',
  'select',
  'forwarding',
  'bind-number',
  'select-image',
  'attach-repository',
  'attach-secrets',
];

/** Review-summary formatting for a generic-store step value (P5 §25 Q2). The
 * named-slice types (attach-storage/policy/webhook) have dedicated Review
 * rows and never come here. Coder-step values carry a `summary` string built
 * "for generic renderers (Q2)" (coder/types.ts:136) — prefer it, fall back to
 * per-type shaping for the numbers steps (which store no label). */
export function formatStepSummary(
  type: AgentTemplateWizardStepType,
  value: unknown,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value === '' ? undefined : value; // text / select
  }
  if (typeof value !== 'object') {
    return undefined;
  }
  const v = value as Record<string, unknown>;
  if (typeof v.summary === 'string' && v.summary !== '') {
    return v.summary; // select-image / attach-repository / attach-secrets
  }
  if (type === 'forwarding') {
    const override = asForwardingOverride(v);
    if (!override) {
      return undefined;
    }
    if (override.enabled === false) {
      return 'Off';
    }
    const bits = [override.audioOut?.enabled ? 'audio replies on' : 'audio replies off'];
    if (override.audioOut?.voiceId) {
      bits.push(`voice ${clip(override.audioOut.voiceId, 16)}`);
    }
    if (override.greeting) {
      bits.push(`greets “${clip(override.greeting, 32)}”`);
    }
    return `On — ${bits.join(', ')}`;
  }
  if (type === 'bind-number') {
    const numberId = v.numberId;
    if (typeof numberId === 'string' && numberId !== '') {
      return `Number ${clip(numberId, 16)} — binds after create`;
    }
    return undefined;
  }
  return undefined;
}

/** Draft guard for the leave-page confirm (§7.3) — anything user-entered counts. */
export function isWizardDirty(draft: WizardDraft, template: AgentTemplate): boolean {
  return (
    draft.basics.name.trim() !== '' ||
    draft.basics.instructions !== (template.defaultInstructions ?? '') ||
    draft.model.modelCollectionId !== null ||
    draft.memory.attach !== (template.memory?.attachStorageMemory === true) ||
    draft.policies.statements.length > 0 ||
    draft.triggers.items.length > 0 ||
    Object.values(draft.steps).some((value) => value !== undefined && value !== '')
  );
}

/**
 * Availability rule (§6.4, computed — never a hardcoded kind list): required
 * template steps whose renderer isn't registered block the card/create.
 */
export function missingRequiredStepTypes(
  entries: WizardEntry[],
  hasRenderer: (type: string) => boolean,
): TemplateWizardStep[] {
  return entries
    .filter(
      (entry): entry is Extract<WizardEntry, { kind: 'template' }> =>
        entry.kind === 'template' && entry.step.required,
    )
    .filter((entry) => entry.forceUnknown || !hasRenderer(entry.step.type))
    .map((entry) => entry.step);
}

/** Badge/placeholder feature wording per §6.4/§16 (audio > coder > additional). */
const FEATURE_GROUP_BY_TYPE: Partial<Record<AgentTemplateWizardStepType, 'audio' | 'coder'>> = {
  forwarding: 'audio',
  'bind-number': 'audio',
  'select-image': 'coder',
  'attach-repository': 'coder',
  'attach-secrets': 'coder',
};

export function featureGroupForTypes(types: string[]): 'audio' | 'coder' | 'additional' {
  let group: 'audio' | 'coder' | 'additional' = 'additional';
  for (const type of types) {
    const candidate = FEATURE_GROUP_BY_TYPE[type as AgentTemplateWizardStepType];
    if (candidate === 'audio') {
      return 'audio';
    }
    if (candidate === 'coder') {
      group = 'coder';
    }
  }
  return group;
}
